import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

/**
 * 初始化Three.js基础组件
 * @param {HTMLElement} container - 渲染容器
 * @param {number} backgroundColor - 背景色（十六进制），默认使用深蓝色
 * @returns {Object} 场景、相机、渲染器、控制器实例
 */
export function initThree (container: HTMLElement, backgroundColor: number = 0x000b1e) {
  // 1. 创建场景（承载所有3D元素）
  const scene = new THREE.Scene()

  // 设置深蓝色背景（对应 rgba(0, 11, 30, 0.95)）
  scene.background = new THREE.Color(backgroundColor)

  // 2. 创建相机（透视相机，模拟人眼视角）
  const camera = new THREE.PerspectiveCamera(
    75, // 视野角度（FOV）
    container.clientWidth / container.clientHeight, // 宽高比
    0.1, // 近裁剪面
    10000 // 远裁剪面
  )
  camera.position.set(5, 5, 5) // 相机初始位置

  // 3. 创建渲染器（WebGL渲染）
  const renderer = new THREE.WebGLRenderer({
    antialias: true, // 抗锯齿开启
    alpha: true, // 开启透明度（用于实现0.95的透明度）
    preserveDrawingBuffer: false // 优化性能
  })

  // 设置渲染器尺寸
  renderer.setSize(container.clientWidth, container.clientHeight)

  // 开启阴影
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap

  // 色调映射设置
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.0

  // 设置输出颜色空间
  if (renderer.outputEncoding !== undefined) {
    renderer.outputEncoding = THREE.sRGBEncoding
  } else if (renderer.outputColorSpace !== undefined) {
    renderer.outputColorSpace = THREE.SRGBColorSpace
  }

  // 设置像素比，提高显示效果
  renderer.setPixelRatio(window.devicePixelRatio)

  // 设置清除颜色和透明度
  renderer.setClearColor(backgroundColor, 0.95) // 透明度设置为0.95

  // 挂载渲染画布
  container.appendChild(renderer.domElement)

  // 4. 创建控制器
  const controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.05
  controls.minDistance = 0.1
  controls.maxDistance = 10000

  // 5. 灯光设置 - 为深色背景增强光照
  const lightIntensity = getLightIntensityByBackground(backgroundColor)

  // 环境光 - 增强以照亮深色背景下的模型
  const ambientLight = new THREE.AmbientLight(0xffffff, lightIntensity.ambient)
  scene.add(ambientLight)

  // 主方向光
  const directionalLight1 = new THREE.DirectionalLight(0xffffff, lightIntensity.directional)
  directionalLight1.position.set(10, 10, 5)
  directionalLight1.castShadow = true
  scene.add(directionalLight1)

  // 补光
  const directionalLight2 = new THREE.DirectionalLight(0xffffff, lightIntensity.fill)
  directionalLight2.position.set(-10, 5, -5)
  scene.add(directionalLight2)

  // 半球光
  const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x888888, lightIntensity.hemisphere)
  hemisphereLight.position.set(0, 20, 0)
  scene.add(hemisphereLight)

  // 点光源
  const pointLight = new THREE.PointLight(0xffffff, lightIntensity.point, 100)
  pointLight.position.set(0, 10, 0)
  scene.add(pointLight)

  // 6. 窗口resize事件
  const handleResize = () => {
    camera.aspect = container.clientWidth / container.clientHeight
    camera.updateProjectionMatrix()
    renderer.setSize(container.clientWidth, container.clientHeight)
  }

  window.addEventListener('resize', handleResize)

  // 7. 添加清理函数
  const dispose = () => {
    window.removeEventListener('resize', handleResize)
    controls.dispose()
    renderer.dispose()
    // 清理场景中的对象
    scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose()
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(m => m.dispose())
        } else {
          object.material.dispose()
        }
      }
    })
  }

  return {
    scene,
    camera,
    renderer,
    controls,
    dispose
  }
}

/**
 * 根据背景色计算合适的光照强度
 */
function getLightIntensityByBackground (bgColor: number): {
  ambient: number
  directional: number
  fill: number
  hemisphere: number
  point: number
} {
  // 深蓝色背景（0x000b1e）需要更强的光照
  // 直接为深蓝色背景返回强光照配置
  if (bgColor === 0x000b1e) {
    return {
      ambient: 1.4, // 增强环境光
      directional: 1.6, // 增强方向光
      fill: 1.0, // 增强补光
      hemisphere: 1.2, // 增强半球光
      point: 0.8 // 增强点光源
    }
  }

  // 原有的亮度计算逻辑（保留用于其他颜色）
  const r = (bgColor >> 16) & 0xFF
  const g = (bgColor >> 8) & 0xFF
  const b = bgColor & 0xFF
  const brightness = (r * 299 + g * 587 + b * 114) / 1000 / 255

  if (brightness < 0.3) {
    // 深色背景，需要更强的光照
    return {
      ambient: 1.2,
      directional: 1.4,
      fill: 0.9,
      hemisphere: 0.9,
      point: 0.6
    }
  } else if (brightness < 0.7) {
    // 中等背景
    return {
      ambient: 1.0,
      directional: 1.2,
      fill: 0.8,
      hemisphere: 0.8,
      point: 0.5
    }
  } else {
    // 浅色背景
    return {
      ambient: 0.8,
      directional: 1.0,
      fill: 0.6,
      hemisphere: 0.6,
      point: 0.4
    }
  }
}

/**
 * 预设颜色常量 - 更新包含深蓝色
 */
export const BackgroundColors = {
  DARK_BLUE: 0x000b1e, // 深蓝色 rgba(0, 11, 30)
  LIGHT_GRAY: 0xf8f9fa, // 浅灰
  WHITE: 0xffffff, // 白色
  APPLE_GRAY: 0xf5f5f7, // 苹果灰
  BLUE_GRAY: 0xf0f2f5, // 蓝灰
  DARK_GRAY: 0xe9ecef, // 深灰
  PAPER: 0xfafafa, // 纸张白
  NIGHT: 0x1a1a2e, // 夜间模式
  DARK_BLUE_OLD: 0x0a192f // 旧深蓝
}
